import { extendComponent } from "@eusoft/webapp-core/Component";
import { type CommitableEditor, formatText, itemsSource, ObjectEditor, required, type IObjectEditorOptions, Message} from "@eusoft/webapp-ui";
import { entityItemsSource } from "../helpers/ItemsSources";
import type { Guid } from "../entities/Commands";
import type { IItemsContainer } from "@eusoft/webapp-ui/abstraction/IItemsContainer";
import "./PermissionEditor.scss"
import type { ResourceScope } from "../entities/Types";


export interface IPermissionsData {
    saleCircuitId: Guid;  
    merchantId: Guid;
    salePointIds: Guid[];
    scopes: string[];
    canMakeReport: boolean;
}

interface IScope {
    scope: ResourceScope;
    includes?: ResourceScope[];
    selectable?: boolean;
}


const validScopes: IScope[] = [{
    scope: "admin",
    includes: ["sell", "charge"],
},
{
    scope: "sell",
},
{
    scope: "charge",
},
{
    scope: "manage_products",
},
]


async function updateScopes(editor: CommitableEditor<string[], unknown, unknown> ){

    await editor.commitAsync();

    const curSelected = [...editor.value];
    
    for (const scope of validScopes) 
        scope.selectable = true;

    for (const scope of validScopes) {

        if (curSelected.includes(scope.scope)) {

            if (!scope.includes)
                continue;

            for (const include of scope.includes) {

                if (!curSelected.includes(include))
                    curSelected.push(include);

                validScopes.find(a => a.scope == include).selectable = false;
            }
        }
    }

    editor.value = curSelected;
}


export const PermissionEditor = extendComponent(ObjectEditor<IPermissionsData>, { }, {
    style: ["vertical"],
    name: "permission-editor",
    commitMode: "manual",
    validationMode: "manual",
    inputField: {
        style: "filled" 
    },
    builder: bld => <>

        {bld.singleSelector(a => a.saleCircuitId, {
            label: "sale-circuit",
            onChanged: () => {
                (bld.container.getPropEditor("merchantId") as IItemsContainer).refreshAsync();
                (bld.container.getPropEditor("salePointIds") as IItemsContainer).refreshAsync();
            },
            validators: [required],
            editor: {
                emptyItem: formatText("[select]") as string,
                itemsSource: entityItemsSource("ListSaleCircuit")
            }
        })}

        {bld.singleSelector(a => a.merchantId, {
            label: "merchant",
            onChanged: () => {
                (bld.container.getPropEditor("salePointIds") as IItemsContainer).refreshAsync();
            },
            editor: {
                emptyItem: formatText("[all]") as string,
                applyFilter: curFilter => ({
                }),
                itemsSource: entityItemsSource("ListMerchant")
            }
        })}

        <Message type="info" style="in-popup">
            {formatText("msg-no-selection-all-sp")}
        </Message>

        {bld.multiSelector(a => a.salePointIds, {
            label: "sale-point",
            editor: {
                applyFilter: curFilter => ({
                    saleCircuitId: bld.container.inputValue.saleCircuitId,
                    merchantId: bld.container.inputValue.merchantId,
                }),
                itemsSource: entityItemsSource("ListSalePoint")
            }
        })}
        
        {bld.boolean(a => a.canMakeReport, {
            label: "can-make-report",
        })}

        {bld.multiSelector(a => a.scopes, { 
            label: "authorizations",
            style: ["vertical-label"],
            validators: [required],
            editor: {
                style: ["scopes", "no-box"],
                createItemView: item => <div className="scope">
                    <div className="name">{formatText("scope-" + item.scope)}</div>
                    <div className="desc">{formatText("scope-" + item.scope + "-desc")}</div> 
                </div>,
                canSelect: item => {
                    return item.selectable !== false;
                },
                onSelectionChanged: async (item, editor) => {
                    updateScopes(editor);
                },
                itemsSource: itemsSource({
                    getItemsAsync: async () => validScopes,
                    getValue: a => a.scope,                    
                }) 
            }
        })}
    </>
} as IObjectEditorOptions<IPermissionsData>);
